import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Table,
    Badge,
    Button,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Pagination,
    PaginationItem,
    PaginationLink
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import { getAllWalletTransactions, resetUserPin, flagWalletTransaction, reverseWalletTransaction } from "../../api/ApiServices";
import Swal from "sweetalert2";

/* ═══════════════════════════════════════════════════
   INLINE STYLES – Premium Glassmorphism & Modern UI
   ═══════════════════════════════════════════════════ */
const styles = {
    headerSection: {
        background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
        padding: "40px 0 100px 0",
        borderRadius: "0 0 40px 40px",
        marginBottom: "-80px"
    },
    filterBar: {
        background: "rgba(255, 255, 255, 0.7)",
        backdropFilter: "blur(12px)",
        borderRadius: "16px",
        padding: "24px",
        margin: "0 0 24px 0",
        border: "1px solid rgba(255, 255, 255, 0.3)",
        boxShadow: "0 8px 32px rgba(31, 38, 135, 0.07)",
    },
    actionCard: {
        background: "#fff",
        borderRadius: "20px",
        border: "none",
        boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
        overflow: "hidden"
    },
    tableRow: {
        transition: "all 0.2s ease",
        cursor: "pointer"
    },
    avatar: {
        width: "36px",
        height: "36px",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "14px",
        color: "#fff",
        flexShrink: 0,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
    },
    infoBlock: {
        background: "rgba(248, 250, 252, 0.8)",
        borderRadius: "12px",
        padding: "16px",
        border: "1px solid #e2e8f0",
        marginBottom: "12px"
    },
    auditTrailLine: {
        borderLeft: "2px solid #e2e8f0",
        paddingLeft: "20px",
        marginLeft: "10px",
        paddingBottom: "20px",
        position: "relative"
    },
    auditDot: {
        width: "12px",
        height: "12px",
        borderRadius: "50%",
        background: "#3b82f6",
        position: "absolute",
        left: "-7px",
        top: "4px",
        border: "3px solid #fff",
        boxShadow: "0 0 0 1px #e2e8f0"
    }
};

/* ═══════════════════════════════════════
   MOCK DATA
   ═══════════════════════════════════════ */
export const DUMMY_TRANSACTIONS = [
    {
        id: "TX-4892A",
        user: { name: "Alice Mwangi", avatarColor: "#5e72e4", email: "alice.m@example.com" },
        type: "Deposit",
        amount: "50,000",
        currency: "KES",
        status: "Completed",
        method: "Mobile Money (M-Pesa)",
        timestamp: "2023-11-20 09:14 AM",
        fundingSource: "M-Pesa Account *3492",
        balanceBefore: "KES 5,200",
        balanceAfter: "KES 55,200",
        allowReverse: false,
        auditTrail: [
            { time: "2023-11-20 09:14 AM", event: "Funds credited automatically via API" },
            { time: "2023-11-20 09:13 AM", event: "Webhook received from Payment Gateway" },
            { time: "2023-11-20 09:12 AM", event: "User initiated deposit request" }
        ]
    },
    {
        id: "TX-4892B",
        user: { name: "Kwame Osei", avatarColor: "#fb6340", email: "kwame.o@example.com" },
        type: "Withdrawal",
        amount: "1,500",
        currency: "GHS",
        status: "Pending",
        method: "Bank Transfer",
        timestamp: "2023-11-20 11:30 AM",
        fundingSource: "Ecobank Ghana Ltd",
        balanceBefore: "GHS 12,000",
        balanceAfter: "GHS 10,500",
        allowReverse: true,
        auditTrail: [
            { time: "2023-11-20 11:30 AM", event: "Withdrawal placed in processing queue" },
            { time: "2023-11-20 11:30 AM", event: "User requested withdrawal" }
        ]
    },
    {
        id: "TX-4892C",
        user: { name: "Nneka Eze", avatarColor: "#11cdef", email: "nneka.e@example.com" },
        type: "Peer Transfer",
        amount: "500",
        currency: "NGN",
        status: "Flagged",
        method: "Internal Wallet",
        timestamp: "2023-11-21 02:45 PM",
        fundingSource: "Wallet Balance",
        balanceBefore: "NGN 8,500",
        balanceAfter: "NGN 8,000",
        allowReverse: true,
        auditTrail: [
            { time: "2023-11-21 02:46 PM", event: "Automated Fraud System flagged transaction (High Velocity)" },
            { time: "2023-11-21 02:45 PM", event: "Transfer initiated to User ID 8891" }
        ]
    },
    {
        id: "TX-4892D",
        user: { name: "Sarah Smith", avatarColor: "#f5365c", email: "sarah.s@example.com" },
        type: "Subscription",
        amount: "19.99",
        currency: "USD",
        status: "Failed",
        method: "Credit Card",
        timestamp: "2023-11-21 06:10 PM",
        fundingSource: "Visa Card *4111",
        balanceBefore: "USD 0.00",
        balanceAfter: "USD 0.00",
        allowReverse: false,
        auditTrail: [
            { time: "2023-11-21 06:10 PM", event: "Payment Gateway returned: Insufficient Funds" },
            { time: "2023-11-21 06:09 PM", event: "Attempting automated monthly charge" }
        ]
    }
];

const getStatusBadge = (status) => {
    const s = status?.toLowerCase();
    switch (s) {
        case "success":
        case "completed":
            return <Badge color="success" style={{ padding: "6px 10px", borderRadius: "6px" }}>{status}</Badge>;
        case "pending":
            return <Badge color="warning" style={{ padding: "6px 10px", borderRadius: "6px" }}>{status}</Badge>;
        case "flagged":
        case "failed":
            return <Badge color="danger" style={{ padding: "6px 10px", borderRadius: "6px" }}>{status}</Badge>;
        case "reversed":
            return <Badge color="secondary" style={{ padding: "6px 10px", borderRadius: "6px" }}>{status}</Badge>;
        default:
            return <Badge color="light" style={{ padding: "6px 10px", borderRadius: "6px", color: "#525f7f" }}>{status}</Badge>;
    }
};

const getTypeBadge = (type) => {
    let color = "secondary";
    const t = type?.toLowerCase();
    if (t === "deposit" || t === "credit") color = "success";
    else if (t === "withdrawal" || t === "debit") color = "warning";
    else if (t === "peer transfer" || t === "transfer") color = "info";
    else if (t === "subscription") color = "primary";

    return (
        <Badge color={color} style={{ background: "transparent", border: `1px solid var(--${color})`, color: `var(--${color})`, padding: "4px 8px" }}>
            {type}
        </Badge>
    );
};

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
const Wallet = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialSearchText = queryParams.get("searchText") || "";
    const highlightId = queryParams.get("highlight");

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filters & Pagination
    const [searchTerm, setSearchTerm] = useState(initialSearchText);
    const [typeFilter, setTypeFilter] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All");
    const [moduleFilter, setModuleFilter] = useState("All");
    const [dateFilter, setDateFilter] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [availableTypes, setAvailableTypes] = useState([]);

    const getFormattedType = (type) => {
        if (!type) return "N/A";
        const t = type.toLowerCase();
        if (t === "credit" || t === "deposit") return "Deposit / Credit";
        if (t === "debit" || t === "withdrawal") return "Withdrawal / Debit";
        if (t === "peer transfer" || t === "transfer") return "Peer Transfer";
        if (t === "subscription") return "Subscription";
        return type.charAt(0).toUpperCase() + type.slice(1);
    };

    const getFormattedModuleType = (source) => {
        if (!source) return "N/A";
        const map = {
            stripe: "Stripe Payment",
            manual: "Manual Adjustment",
            bonus: "Bonus Reward",
            tip_sent: "Tip Sent",
            tip_received: "Tip Received",
            post_promotion: "Post Promotion",
            investment: "Investment Made",
            investment_received: "Investment Received",
            project_funding: "Project Funding",
            escrow_release: "Escrow Release",
            referral: "Referral Bonus",
            challenge_publication: "Challenge Publication",
            challenge_prize: "Challenge Prize",
            membership_purchase: "Membership Purchase",
            membership_received: "Membership Received"
        };
        const s = source.toLowerCase();
        if (map[s]) return map[s];
        return source.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };


    const getImageUrl = (img, name) => {
        const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "User")}&background=5e72e4&color=fff`;
        if (!img) return fallback;
        if (img.startsWith("http")) return img;
        return `https://cradleaibucket.s3.us-east-1.amazonaws.com/users-profile-images/${img}`;
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const search = params.get("searchText") || "";
        setSearchTerm(search);
    }, [location.search]);

    // Modal State
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedTxn, setSelectedTxn] = useState(null);

    const renderPaginationItems = (currentPage, totalItems, limit, onPageChange) => {
        const totalPagesCount = Math.ceil(totalItems / limit);
        if (totalPagesCount <= 1) return null;

        const pages = [];
        const delta = 2;

        pages.push(
            <PaginationItem key={1} active={currentPage === 1}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(1); }}>1</PaginationLink>
            </PaginationItem>
        );

        if (currentPage > delta + 2) {
            pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        const rangeStart = Math.max(2, currentPage - delta);
        const rangeEnd = Math.min(totalPagesCount - 1, currentPage + delta);

        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(
                <PaginationItem key={i} active={currentPage === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        if (currentPage < totalPagesCount - delta - 1) {
            pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        if (totalPagesCount > 1) {
            pages.push(
                <PaginationItem key={totalPagesCount} active={currentPage === totalPagesCount}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(totalPagesCount); }}>{totalPagesCount}</PaginationLink>
                </PaginationItem>
            );
        }

        return pages;
    };

    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        try {
            const status = statusFilter === "All" ? "" : statusFilter.toLowerCase();
            const type = typeFilter === "All" ? "" : typeFilter.toLowerCase();
            const source = moduleFilter === "All" ? "" : moduleFilter;
            console.log("Fetching transactions with:", { page, searchTerm, status, type, source, dateFilter });
            const response = await getAllWalletTransactions(
                page,
                limit,
                searchTerm,
                status,
                type,
                dateFilter,
                "",
                source
            );
            console.log("API Response in Wallet component:", response);
            if (response && response.payload) {
                setTransactions(Array.isArray(response.payload.walletTransactions) ? response.payload.walletTransactions : []);
                setTotalPages(response.payload.totalPages || 1);
                setTotalResults(response.payload.total || 0);
                if (response.payload.types && response.payload.types.length > 0) {
                    setAvailableTypes(response.payload.types);
                }
            } else {
                setTransactions([]);
                setTotalResults(0);
            }
        } catch (error) {
            console.error("Error fetching transactions:", error);
            Swal.fire("Error", "Failed to load wallet transactions", "error");
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    }, [page, limit, searchTerm, statusFilter, typeFilter, moduleFilter, dateFilter]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    // Reset page when filters change
    useEffect(() => {
        setPage(1);
    }, [searchTerm, typeFilter, statusFilter, moduleFilter, dateFilter]);

    const handleAction = (action, txn, event) => {
        if (event) event.preventDefault();

        if (action === "Review") {
            setSelectedTxn(txn);
            setDrawerOpen(true);
            return;
        }

        if (action === "Reset PIN") {
            Swal.fire({
                title: "Administrative PIN Reset",
                html: `
                    <div class="text-left mb-3">
                        <label class="form-control-label">New 4-Digit PIN</label>
                        <input id="swal-pin" class="form-control" type="password" maxlength="4" placeholder="••••">
                    </div>
                    <div class="text-left">
                        <label class="form-control-label">Reason for Reset (Audit Log)</label>
                        <textarea id="swal-reason" class="form-control" rows="3" placeholder="e.g. User lost access, Security override requested by user..."></textarea>
                    </div>
                `,
                icon: "shield-alt",
                showCancelButton: true,
                confirmButtonColor: "#5e72e4",
                cancelButtonColor: "#f5365c",
                confirmButtonText: "Execute Reset",
                preConfirm: () => {
                    const pin = document.getElementById('swal-pin').value;
                    const reason = document.getElementById('swal-reason').value;
                    if (!pin || pin.length !== 4 || !/^\d+$/.test(pin)) {
                        Swal.showValidationMessage("Please enter a valid 4-digit PIN");
                        return false;
                    }
                    if (!reason || reason.trim().length < 5) {
                        Swal.showValidationMessage("Please provide a detailed reason (min 5 chars)");
                        return false;
                    }
                    return { pin, reason };
                }
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        Swal.fire({
                            title: 'Processing...',
                            onBeforeOpen: () => Swal.showLoading(),
                            allowOutsideClick: false,
                            showConfirmButton: false
                        });

                        await resetUserPin(txn.userId?._id || txn.userId, {
                            pin: result.value.pin,
                            reason: result.value.reason
                        });

                        Swal.fire({ title: "Success!", text: `PIN has been reset and notified to ${txn.userId?.email || 'user'}.`, icon: "success", timer: 3000, showConfirmButton: false });
                    } catch (err) {
                        Swal.fire("Failed", err.response?.data?.message || "Failed to reset PIN", "error");
                    }
                }
            });
            return;
        }

        Swal.fire({
            title: `${action} Transaction`,
            html: `
                <p>Are you sure you want to ${action.toLowerCase()} transaction <strong>${txn.transactionId || txn._id}</strong>?</p>
                <div class="text-left mt-3">
                    <label class="form-control-label">Reason (Audit Log)</label>
                    <textarea id="swal-action-reason" class="form-control" rows="3" placeholder="Provide a reason for this action..."></textarea>
                </div>
            `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#5e72e4",
            cancelButtonColor: "#f5365c",
            confirmButtonText: "Yes, proceed",
            cancelButtonText: "Cancel",
            preConfirm: () => {
                const reason = document.getElementById('swal-action-reason').value;
                if (!reason || reason.trim().length < 5) {
                    Swal.showValidationMessage("Please provide a detailed reason (min 5 chars)");
                    return false;
                }
                return { reason: reason.trim() };
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    Swal.fire({
                        title: 'Processing...',
                        allowOutsideClick: false,
                        showConfirmButton: false,
                        didOpen: () => Swal.showLoading()
                    });

                    if (action === "Flag") {
                        await flagWalletTransaction(txn._id, result.value.reason);
                    } else if (action === "Reverse") {
                        await reverseWalletTransaction(txn._id, result.value.reason);
                        setDrawerOpen(false);
                    }

                    await fetchTransactions();

                    Swal.fire({
                        title: "Done!",
                        text: `Action '${action}' applied successfully and logged to audit trail.`,
                        icon: "success",
                        timer: 3000,
                        showConfirmButton: false,
                    });
                } catch (err) {
                    Swal.fire("Failed", err.response?.data?.message || `Failed to ${action.toLowerCase()} transaction`, "error");
                }
            }
        });
    };

    // Custom CSS for Dropdowns
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .dropdown-menu { z-index: 9999 !important; position: absolute !important; will-change: transform; }
            .btn-group .dropdown-menu { z-index: 9999 !important; }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    return (
        <>
            <CommonHeader />
            <Container className="" fluid style={{ position: "relative", zIndex: 1, marginTop: "-108px" }} >

                {/* ── Main Card ─────────────────────────────────── */}
                <Row>
                    <Col>
                        {/* ── Filter Glass Bar ─────────────────────── */}
                        <div style={{ ...styles.filterBar, flexWrap: "nowrap", overflowX: "auto" }} className="d-flex align-items-center mobile-wallet">
                            <div style={{ flex: "1 1 200px", minWidth: "180px", marginRight: "10px" }}>
                                <InputGroup className="input-group-alternative" style={{ borderRadius: "12px", border: "1px solid #e2e8f0", flexWrap: "nowrap", }}>
                                    <InputGroupAddon addonType="prepend">
                                        <InputGroupText style={{ background: "transparent", border: "none" }}>
                                            <i className="fas fa-search text-muted " />
                                        </InputGroupText>
                                    </InputGroupAddon>
                                    <Input
                                        placeholder="Search User, Transaction ID..."
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ border: "none", height: "45px", paddingLeft: "10px", minWidth: 0 }}
                                    />
                                </InputGroup>
                            </div>
                            <div style={{ flex: "0 0 auto", marginRight: "10px" }} className="selectt-box">
                                <Input
                                    type="select"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    style={{ borderRadius: "12px", height: "45px", border: "1px solid #e2e8f0", width: "100%" }}
                                >
                                    <option value="All">All Types</option>
                                    {(() => {
                                        const rawTypes = availableTypes.length > 0 ? availableTypes : ["deposit", "withdrawal", "peer transfer", "subscription"];
                                        const seen = new Set();
                                        const uniqueOptions = [];
                                        
                                        rawTypes.forEach((type) => {
                                            const formatted = getFormattedType(type);
                                            if (!seen.has(formatted)) {
                                                seen.add(formatted);
                                                uniqueOptions.push({ value: type, label: formatted });
                                            }
                                        });
                                        
                                        return uniqueOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ));
                                    })()}
                                </Input>
                            </div>
                            <div style={{ flex: "0 0 auto", marginRight: "10px" }} className="selectt-box">
                                <Input
                                    type="select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    style={{ borderRadius: "12px", height: "45px", border: "1px solid #e2e8f0", width: "100%" }}
                                >
                                    <option value="All">All Statuses</option>
                                    <option value="Success">Success</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Failed">Failed</option>
                                </Input>
                            </div>
                            <div style={{ flex: "0 0 auto", width: "170px", marginRight: "10px" }} className="selectt-box">
                                <Input
                                    type="select"
                                    value={moduleFilter}
                                    onChange={(e) => setModuleFilter(e.target.value)}
                                    style={{ borderRadius: "12px", height: "45px", border: "1px solid #e2e8f0",  }}
                                >
                                    <option value="All">All Module Types</option>
                                    <option value="stripe">Stripe Payment</option>
                                    <option value="manual">Manual Adjustment</option>
                                    <option value="bonus">Bonus Reward</option>
                                    <option value="tip_sent">Tip Sent</option>
                                    <option value="tip_received">Tip Received</option>
                                    <option value="post_promotion">Post Promotion</option>
                                    <option value="investment">Investment Made</option>
                                    <option value="investment_received">Investment Received</option>
                                    <option value="project_funding">Project Funding</option>
                                    <option value="escrow_release">Escrow Release</option>
                                    <option value="referral">Referral Bonus</option>
                                    <option value="challenge_publication">Challenge Publication</option>
                                    <option value="challenge_prize">Challenge Prize</option>
                                    <option value="membership_purchase">Membership Purchase</option>
                                    <option value="membership_received">Membership Received</option>
                                </Input>
                            </div>
                            <div style={{ flex: "0 0 auto", }} className="selectt-box">
                                <Input
                                    type="date"
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                    max={new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0]}
                                    style={{ borderRadius: "12px", height: "45px", border: "1px solid #e2e8f0", width: "100%" }}
                                />
                            </div>
                        </div>

                        <Card style={styles.actionCard}>
                            <CardHeader className="bg-transparent border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                                <h3 className="mb-0 font-weight-bold text-dark">
                                    <i className="ni ni-credit-card text-blue mr-2" />
                                    Wallet Transactions
                                </h3>
                                <Badge color="primary" pill style={{ fontSize: "12px", padding: "8px 12px" }}>
                                    {totalResults} Transactions
                                </Badge>
                            </CardHeader>

                            {/* ── Transaction Table ─────────────────────── */}
                            <CardBody className="p-0">
                                <div
                                    className="table-responsive custom-horizontal-scrollbar"
                                    style={{ border: "1px solid #e9ecef", overflowX: "auto" }}
                                >
                                    <style>{`
                                        .custom-horizontal-scrollbar::-webkit-scrollbar {
                                            height: 8px;
                                            width: 8px;
                                            display: block !important;
                                        }
                                        .custom-horizontal-scrollbar::-webkit-scrollbar-track {
                                            background: #f4f5f7;
                                            border-radius: 10px;
                                        }
                                        .custom-horizontal-scrollbar::-webkit-scrollbar-thumb {
                                            background: #c1c1c1;
                                            border-radius: 10px;
                                        }
                                        .custom-horizontal-scrollbar::-webkit-scrollbar-thumb:hover {
                                            background: #a8a8a8;
                                        }
                                    `}</style>
                                    <Table className="align-items-center table-flush" hover style={{ minWidth: "1200px" }}>
                                        <thead className="thead-light" style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#f6f9fc" }}>
                                            <tr>
                                                <th scope="col" style={{ padding: "16px 24px", position: "sticky", top: 0, background: "#f6f9fc", zIndex: 11, whiteSpace: "nowrap" }}>TxID / Time</th>
                                                <th scope="col" style={{ position: "sticky", top: 0, background: "#f6f9fc", zIndex: 11, whiteSpace: "nowrap" }}>User</th>
                                                <th scope="col" style={{ position: "sticky", top: 0, background: "#f6f9fc", zIndex: 11, whiteSpace: "nowrap" }}>Type</th>
                                                <th scope="col" style={{ position: "sticky", top: 0, background: "#f6f9fc", zIndex: 11, whiteSpace: "nowrap" }}>Amount</th>
                                                <th scope="col" style={{ position: "sticky", top: 0, background: "#f6f9fc", zIndex: 11, whiteSpace: "nowrap" }}>Status</th>
                                                <th scope="col" style={{ position: "sticky", top: 0, background: "#f6f9fc", zIndex: 11, whiteSpace: "nowrap" }}>Method</th>
                                                <th scope="col" style={{ position: "sticky", top: 0, background: "#f6f9fc", zIndex: 11, whiteSpace: "nowrap" }}>Module Type</th>

                                                <th scope="col" className="text-right" style={{ paddingRight: "24px", position: "sticky", top: 0, background: "#f6f9fc", zIndex: 11, whiteSpace: "nowrap" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-5">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="sr-only">Loading...</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : transactions.length > 0 ? transactions.map((txn) => (
                                                <tr key={txn._id} style={{
                                                    ...styles.tableRow,
                                                    background: highlightId === txn._id ? "#fffbf0" : "transparent"
                                                }}>
                                                    <td style={{ padding: "16px 24px" }}>
                                                        <div className="font-weight-bold" style={{ color: "#32325d" }}>{txn.transactionId}</div>
                                                        <div className="text-muted text-xs mt-1"><i className="far fa-clock mr-1" />{new Date(txn.createdAt).toLocaleString()}</div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">

                                                            {/* User Profile Image */}
                                                            <img
                                                                src={getImageUrl(txn?.userId?.image, txn?.userId?.name)}
                                                                alt={txn?.userId?.name || "User"}
                                                                onError={(e) => {
                                                                    e.currentTarget.onerror = null;
                                                                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(txn?.userId?.name || "User")}&background=5e72e4&color=fff`;
                                                                }}
                                                                style={{
                                                                    width: "55px",
                                                                    height: "55px",
                                                                    borderRadius: "50%",
                                                                    objectFit: "cover",
                                                                    marginRight: "12px",
                                                                    border: "3px solid #5e72e4",
                                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                                                }}
                                                            />
                                                            {/* User Name & Email */}
                                                            <div>
                                                                <span className="font-weight-bold d-block">
                                                                    {txn.userId?.name || "Unknown User"}
                                                                </span>

                                                                <span className="text-xs text-muted">
                                                                    {txn.userId?.email || "N/A"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{getTypeBadge(txn.type || "N/A")}</td>
                                                    <td>
                                                        <span className="font-weight-bold" style={{ fontSize: "14px", color: (txn.type === "Deposit" || txn.type === "deposit" || txn.type === "credit") ? "#2dce89" : "#32325d" }}>
                                                            {txn.currency?.toUpperCase() || "USD"} {txn.amount}
                                                        </span>
                                                    </td>
                                                    <td>{getStatusBadge(txn.status)}</td>
                                                    <td>
                                                        <span className="text-sm font-weight-bold text-muted">{txn.method}</span>
                                                    </td>
                                                    <td>
                                                        <span className="text-sm font-weight-bold text-muted">{getFormattedModuleType(txn.source)}</span>
                                                    </td>


                                                    <td className="text-right" style={{ paddingRight: "24px", position: "relative" }}>
                                                        <div className="d-flex justify-content-end align-items-center">
                                                            <Button
                                                                className="btn-icon-only rounded-circle mr-2 shadow"
                                                                color="primary"
                                                                size="sm"
                                                                style={{ width: "32px", height: "32px", padding: 0 }}
                                                                onClick={(e) => handleAction("Review", txn, e)}
                                                                title="View Details"
                                                            >
                                                                <i className="fas fa-eye" style={{ fontSize: "12px" }} />
                                                            </Button>
                                                            <UncontrolledDropdown>
                                                                <DropdownToggle
                                                                    className="btn-icon-only shadow-sm"
                                                                    role="button"
                                                                    size="sm"
                                                                    style={{
                                                                        background: "#fff",
                                                                        border: "none",
                                                                        borderRadius: "8px",
                                                                        color: "#6c757d",
                                                                        width: "32px",
                                                                        height: "32px",
                                                                        padding: 0,
                                                                        boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 4px 6px rgba(50,50,93,0.11)"
                                                                    }}
                                                                    onClick={(e) => e.preventDefault()}
                                                                >
                                                                    <i className="fas fa-ellipsis-v" />
                                                                </DropdownToggle>
                                                                <DropdownMenu className="dropdown-menu-arrow shadow border-0" right>
                                                                    <DropdownItem onClick={(e) => handleAction("Flag", txn, e)}>
                                                                        <i className="fas fa-flag text-warning mr-2" /> Flag Transaction
                                                                    </DropdownItem>
                                                                    {(txn.allowReverse || (txn.type === "Withdrawal" && txn.status === "pending")) && (
                                                                        <DropdownItem onClick={(e) => handleAction("Reverse", txn, e)}>
                                                                            <i className="fas fa-undo text-danger mr-2" /> Reverse Funds
                                                                        </DropdownItem>
                                                                    )}
                                                                </DropdownMenu>
                                                            </UncontrolledDropdown>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-5">
                                                        <div className="text-muted"><i className="fas fa-inbox fa-3x mb-3 d-block" />No transactions found.</div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>

                            {/* ── Pagination ─────────────────────────── */}
                            <CardFooter className="py-4 border-top bg-transparent d-flex justify-content-between align-items-center">
                                <span className="text-muted small">
                                    Showing <span className="font-weight-bold">{((page - 1) * limit) + 1} to {Math.min(page * limit, totalResults)}</span> of <span className="font-weight-bold">{totalResults}</span> transactions
                                </span>
                                <nav aria-label="...">
                                    <Pagination
                                        className="pagination justify-content-end mb-0"
                                        listClassName="justify-content-end mb-0"
                                    >
                                        <PaginationItem disabled={page === 1}>
                                            <PaginationLink
                                                onClick={(e) => { e.preventDefault(); setPage(page - 1); }}
                                                tabIndex="-1"
                                            >
                                                <i className="fas fa-angle-left" />
                                            </PaginationLink>
                                        </PaginationItem>
                                        {renderPaginationItems(page, totalResults, limit, setPage)}
                                        <PaginationItem disabled={page === totalPages}>
                                            <PaginationLink onClick={(e) => { e.preventDefault(); setPage(page + 1); }}>
                                                <i className="fas fa-angle-right" />
                                            </PaginationLink>
                                        </PaginationItem>
                                    </Pagination>
                                </nav>
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* ── Transaction Detail Drawer / Modal ─────────────────── */}
            {selectedTxn && (
                <Modal isOpen={drawerOpen} toggle={() => setDrawerOpen(false)} size="lg" centered>
                    <ModalHeader toggle={() => setDrawerOpen(false)} className="bg-secondary border-0 pb-0">
                        <div className="d-flex align-items-center">
                            <span className="font-weight-bold" style={{ fontSize: "18px" }}>Transaction Detail</span>
                            <Badge color="light" className="ml-3" style={{ fontSize: "13px", padding: "6px 10px" }}>{selectedTxn.id}</Badge>
                        </div>
                    </ModalHeader>
                    <ModalBody className="p-0 bg-secondary">
                        <Row className="m-0">
                            {/* Left Side Context */}
                            <Col md="8" className="p-4 border-right bg-white" style={{ borderBottomLeftRadius: "16px" }}>

                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div>
                                        <h2 className="mb-0 text-dark font-weight-900" style={{ fontSize: "28px", letterSpacing: "-0.5px" }}>
                                            {selectedTxn.currency || "USD"} {selectedTxn.amount}
                                        </h2>
                                        <div className="text-muted mt-1">{getTypeBadge(selectedTxn.type)} &nbsp; • &nbsp; {getStatusBadge(selectedTxn.status)}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-muted text-xs text-uppercase font-weight-bold">Timestamp</div>
                                        <div className="font-weight-bold text-dark">{new Date(selectedTxn.createdAt).toLocaleString()}</div>
                                    </div>
                                </div>

                                <Row>
                                    <Col md="6">
                                        <div style={styles.infoBlock}>
                                            <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">Funding Source / Method</div>
                                            <div className="font-weight-bold text-dark">
                                                <i className="fas fa-money-check-alt text-primary mr-2" />{selectedTxn.method || "N/A"}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <div style={styles.infoBlock}>
                                            <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">User Wallet</div>
                                            <div className="d-flex align-items-start">
                                                <div
                                                    style={{
                                                        ...styles.avatar,
                                                        background: "#5e72e4",
                                                        marginRight: "10px",
                                                        width: "26px",
                                                        height: "26px",
                                                        fontSize: "11px",
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    {selectedTxn.userId?.name?.charAt(0) ||
                                                        selectedTxn.userId?.email?.charAt(0) ||
                                                        "U"}
                                                </div>

                                                <span
                                                    className="font-weight-bold text-dark"
                                                    style={{
                                                        wordBreak: "break-all", whiteSpace: "normal"
                                                    }}
                                                >
                                                    {selectedTxn.userId?.email ||
                                                        selectedTxn.userId?.name ||
                                                        "Unknown"}
                                                </span>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <div style={styles.infoBlock} className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">Balance Before</div>
                                            <div className="font-weight-bold text-dark">{selectedTxn.oldBalance || "N/A"}</div>
                                        </div>
                                        <div className="text-muted"><i className="fas fa-long-arrow-alt-right" /></div>
                                        <div className="text-right">
                                            <div className="text-muted text-xs text-uppercase font-weight-bold mb-1">Balance After</div>
                                            <div className="font-weight-bold text-dark">{selectedTxn.currentBalance || "N/A"}</div>
                                        </div>
                                    </div>
                                </div>

                                <h6 className="text-uppercase text-muted ls-1 mb-3">Audit Trail / Lifecycle</h6>
                                <div>
                                    {selectedTxn.auditTrail ? selectedTxn.auditTrail.map((audit, idx) => (
                                        <div key={idx} style={styles.auditTrailLine}>
                                            <div style={styles.auditDot} />
                                            <div className="text-xs text-muted mb-1">{audit.time}</div>
                                            <div className="text-sm text-dark font-weight-bold">{audit.event}</div>
                                        </div>
                                    )) : (
                                        <div className="text-muted text-sm italic">No specific audit trail available for this transaction.</div>
                                    )}
                                </div>

                            </Col>

                            {/* Right Side Admin Actions */}
                            <Col md="4" className="p-4 pt-5">
                                <h6 className="text-uppercase text-muted ls-1 mb-3">Admin Actions</h6>
                                {/* 
                                <Button block color="warning" className="mb-3 text-left" onClick={() => handleAction("Flag", selectedTxn)} disabled={selectedTxn.status === "Flagged"}>
                                    <i className="fas fa-flag mr-2" /> Flag Transaction
                                </Button> */}

                                {selectedTxn.allowReverse && (
                                    <Button block color="danger" className="mb-3 text-left" outline onClick={() => handleAction("Reverse", selectedTxn)}>
                                        <i className="fas fa-undo mr-2" /> Reverse Funds
                                    </Button>
                                )}

                                <hr className="my-4 border-light" />

                                <Button block color="primary" className="mb-0 text-left" outline onClick={() => handleAction("Reset PIN", selectedTxn)}>
                                    <i className="fas fa-key mr-2" /> Reset User PIN
                                </Button>
                                <small className="text-muted d-block mt-2">Sends a secure PIN reset link to the user's registered email address.</small>
                            </Col>
                        </Row>
                    </ModalBody>
                </Modal>
            )}
        </>
    );
};

export default Wallet;
